import React from 'react';
import { Form, Select } from '@duxton-web/ui';
import styled from 'styled-components';

import TreatmentAssignmentInput from '@/pages/ExperimentFormV2/common/TreatmentAssignmentInput';
import { useTreatments } from '@/pages/ExperimentFormV2/contexts/ExperimentFormInfoProvider';
import {
  CustomTimeSliceStrategy,
  ExperimentAssignmentStrategy,
  ExpFormField,
} from '@/pages/ExperimentFormV2/helper/consts';

interface AssignmentStrategyInputCarouselProps {
  isViewMode?: boolean;
}

const getTimeTitleFromMinute = (minute: number) => {
  const hour = Math.floor(minute / 60);
  const minuteInHour = minute % 60;
  if (hour === 0) {
    return `${minuteInHour}m`;
  }
  if (minuteInHour === 0) {
    return `${hour}h`;
  }
  return `${hour}h${minuteInHour}m`;
};

// make an array from 15min to 1440min (24hr) with step interval of 15min
const balancedShuffleDurationOptions = Array.from(
  { length: 96 },
  (_v, k) => k * 15 + 15,
).map((duration) => ({
  label: getTimeTitleFromMinute(duration),
  value: duration * 60,
}));

const AssignmentStrategyInputCarousel: React.FC<
  AssignmentStrategyInputCarouselProps
> = ({ isViewMode }) => {
  const [treatments] = useTreatments();
  const form = Form.useFormInstance();
  const currentStrategyName = Form.useWatch(ExpFormField.STRATEGY.NAME, {
    form,
    preserve: true,
  });
  const currentCustomTimeSliceStrategyName = Form.useWatch(
    ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_NAME,
    {
      form,
      preserve: true,
    },
  );
  const currentCustomTimeSliceStrategyDuration = Form.useWatch(
    ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DURATION,
    {
      form,
      preserve: true,
    },
  );

  const isBalancedShuffleTimeSlice =
    currentStrategyName === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
    currentCustomTimeSliceStrategyName ===
      CustomTimeSliceStrategy.BALANCED_SHUFFLE;

  if (isBalancedShuffleTimeSlice) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Title>Time slice duration</Title>
        <Subtitle>Duration defined will be used across all treatments</Subtitle>
        <Form.Item
          name={ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DURATION}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: 'Required',
            },
          ]}
        >
          <Select
            disabled={isViewMode}
            value={currentCustomTimeSliceStrategyDuration}
            onChange={(value) => {
              form.setFieldValue(
                ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DURATION,
                value,
              );
            }}
            options={balancedShuffleDurationOptions}
          />
        </Form.Item>
      </div>
    );
  }
  return (
    <InputCarousel>
      {treatments.map((treatment) => (
        <TreatmentAssignmentInput
          treatmentName={treatment.name}
          treatmentId={treatment.id}
          strategy={currentStrategyName}
          color={treatment.color}
          key={treatment.id}
          disabled={isViewMode}
        />
      ))}
    </InputCarousel>
  );
};

const InputCarousel = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  overflow: auto;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: #707070;
`;

export default AssignmentStrategyInputCarousel;
